import { EducationalPathUserDTO } from '../dto/educational-path/educational-path-user.dto';

export interface MaintenanceRollBounds {
  start: Date;
  end: Date;
}

export interface MaintenanceRollRecurrenceInput {
  recurrence_type?: string | null;
  recurrence_value?: number | null;
  recurrence_unit?: string | null;
  recurrence_calendar_anchor_date?: string | null;
  version_created_at?: string | null;
  maintenance_started_at?: string | null;
  reset_on_fulfillment?: boolean;
  last_maintenance_fulfilled_at?: string | null;
  recurrence_window_start?: string | null;
  recurrence_window_end?: string | null;
}

export function profileYearToReferenceDate(year: number): string {
  return `${year}-07-01`;
}

function parseDateOnly(value: string | Date): Date {
  if (value instanceof Date) {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  const s = String(value).trim();
  const iso = s.length >= 10 ? s.slice(0, 10) : s;
  const [y, m, day] = iso.split('-').map((n) => parseInt(n, 10));
  return new Date(y, (m || 1) - 1, day || 1, 0, 0, 0, 0);
}

function endOfDay(d: Date): Date {
  const e = new Date(d);
  e.setHours(23, 59, 59, 999);
  return e;
}

function daysBetween(a: Date, b: Date): number {
  const ms = parseDateOnly(a).getTime() - parseDateOnly(b).getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

/** Months elapsed from anchor to ref (PostgreSQL age-like, calendar months). */
function monthsElapsed(anchor: Date, ref: Date): number {
  const a = parseDateOnly(anchor);
  const r = parseDateOnly(ref);
  return (r.getFullYear() - a.getFullYear()) * 12 + (r.getMonth() - a.getMonth());
}

function defaultVersionAnchor(createdAt: string | null | undefined): Date {
  if (createdAt) {
    const d = new Date(createdAt);
    if (!isNaN(d.getTime())) {
      return new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
    }
  }
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
}

function resolvePerUserAnchor(input: MaintenanceRollRecurrenceInput): Date | null {
  const type = input.recurrence_type || 'calendar';
  if (type !== 'per_user') {
    return null;
  }
  if (input.reset_on_fulfillment) {
    if (input.last_maintenance_fulfilled_at) {
      return parseDateOnly(input.last_maintenance_fulfilled_at);
    }
    if (input.maintenance_started_at) {
      return parseDateOnly(input.maintenance_started_at);
    }
    return null;
  }
  if (input.maintenance_started_at) {
    return parseDateOnly(input.maintenance_started_at);
  }
  return null;
}

export function isMultiYearRoll(
  value: number | null | undefined,
  unit: string | null | undefined
): boolean {
  const u = (unit ?? 'year').toLowerCase();
  const v = Math.max(value ?? 1, 1);
  if (u === 'full_period') return false;
  if (u === 'year' || u === 'years') return v > 1;
  if ((u === 'day' || u === 'days') && v === 365) return false;
  if ((u === 'day' || u === 'days') && v % 365 === 0 && v >= 365) {
    return v / 365 > 1;
  }
  return false;
}

export function computeMaintenanceRollBounds(
  input: MaintenanceRollRecurrenceInput,
  referenceDate: string | Date
): MaintenanceRollBounds | null {
  const type = input.recurrence_type || 'calendar';
  let value = Math.max(input.recurrence_value ?? 1, 1);
  const unit = (input.recurrence_unit ?? 'year').toLowerCase();
  const ref = parseDateOnly(referenceDate);

  if (type === 'per_user') {
    const anchor = resolvePerUserAnchor(input);
    if (!anchor) {
      return null;
    }

    if (unit === 'full_period') {
      return { start: anchor, end: endOfDay(new Date(2099, 11, 31)) };
    }

    if (unit === 'year' || unit === 'years') {
      const months = monthsElapsed(anchor, ref);
      const blockIndex = Math.floor(months / (value * 12));
      const start = new Date(anchor);
      start.setFullYear(start.getFullYear() + blockIndex * value);
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + value);
      end.setDate(end.getDate() - 1);
      return { start: parseDateOnly(start), end: endOfDay(end) };
    }

    if (unit === 'month' || unit === 'months') {
      const months = monthsElapsed(anchor, ref);
      const blockIndex = Math.floor(months / value);
      const start = new Date(anchor);
      start.setMonth(start.getMonth() + blockIndex * value);
      const end = new Date(start);
      end.setMonth(end.getMonth() + value);
      end.setDate(end.getDate() - 1);
      return { start: parseDateOnly(start), end: endOfDay(end) };
    }

    if (unit === 'day' || unit === 'days') {
      const periodDays = value === 365 ? 365 : value;
      const blockIndex = Math.floor(daysBetween(ref, anchor) / periodDays);
      const start = new Date(anchor);
      start.setDate(start.getDate() + blockIndex * periodDays);
      const end = new Date(start);
      end.setDate(end.getDate() + periodDays - 1);
      return { start: parseDateOnly(start), end: endOfDay(end) };
    }

    if (unit === 'week' || unit === 'weeks') {
      const periodDays = value * 7;
      const blockIndex = Math.floor(daysBetween(ref, anchor) / periodDays);
      const start = new Date(anchor);
      start.setDate(start.getDate() + blockIndex * periodDays);
      const end = new Date(start);
      end.setDate(end.getDate() + periodDays - 1);
      return { start: parseDateOnly(start), end: endOfDay(end) };
    }

    const blockIndex = Math.floor(daysBetween(ref, anchor) / 365);
    const start = new Date(anchor);
    start.setDate(start.getDate() + blockIndex * 365);
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    end.setDate(end.getDate() - 1);
    return { start: parseDateOnly(start), end: endOfDay(end) };
  }

  // Calendar
  if (unit === 'full_period') {
    const anchor = input.recurrence_calendar_anchor_date
      ? parseDateOnly(input.recurrence_calendar_anchor_date)
      : defaultVersionAnchor(input.version_created_at);
    return { start: anchor, end: endOfDay(new Date(2099, 11, 31)) };
  }

  if (
    unit === 'year' ||
    unit === 'years' ||
    (unit === 'day' && value === 365)
  ) {
    if (unit === 'day' && value === 365) {
      value = 1;
    }
    if (value === 1) {
      const start = new Date(ref.getFullYear(), 0, 1);
      const end = new Date(ref.getFullYear(), 11, 31);
      return { start, end: endOfDay(end) };
    }
    const anchor = input.recurrence_calendar_anchor_date
      ? parseDateOnly(input.recurrence_calendar_anchor_date)
      : defaultVersionAnchor(input.version_created_at);
    const anchorYear = anchor.getFullYear();
    const refYear = ref.getFullYear();
    const blockStartYear =
      anchorYear +
      Math.floor((refYear - anchorYear) / value) * value;
    const start = new Date(blockStartYear, 0, 1);
    const end = new Date(blockStartYear + value - 1, 11, 31);
    return { start, end: endOfDay(end) };
  }

  const anchor = input.recurrence_calendar_anchor_date
    ? parseDateOnly(input.recurrence_calendar_anchor_date)
    : defaultVersionAnchor(input.version_created_at);

  if (unit === 'month' || unit === 'months') {
    const months = monthsElapsed(anchor, ref);
    const blockIndex = Math.floor(months / value);
    const start = new Date(anchor);
    start.setMonth(start.getMonth() + blockIndex * value);
    const end = new Date(start);
    end.setMonth(end.getMonth() + value);
    end.setDate(end.getDate() - 1);
    return { start: parseDateOnly(start), end: endOfDay(end) };
  }

  if (unit === 'day' || unit === 'days') {
    const periodDays = value;
    const blockIndex = Math.floor(daysBetween(ref, anchor) / periodDays);
    const start = new Date(anchor);
    start.setDate(start.getDate() + blockIndex * periodDays);
    const end = new Date(start);
    end.setDate(end.getDate() + periodDays - 1);
    return { start: parseDateOnly(start), end: endOfDay(end) };
  }

  const start = new Date(ref.getFullYear(), 0, 1);
  const end = new Date(ref.getFullYear(), 11, 31);
  return { start, end: endOfDay(end) };
}

export function boundsFromApiWindow(
  start: string | null | undefined,
  end: string | null | undefined
): MaintenanceRollBounds | null {
  if (!start || !end) {
    return null;
  }
  const s = parseDateOnly(start);
  const e = endOfDay(parseDateOnly(end));
  if (isNaN(s.getTime()) || isNaN(e.getTime())) {
    return null;
  }
  return { start: s, end: e };
}

export function getRollBoundsForProfileYear(
  path: MaintenanceRollRecurrenceInput,
  year: number,
  preferApiWindow = true
): MaintenanceRollBounds | null {
  if (preferApiWindow) {
    const fromApi = boundsFromApiWindow(
      path.recurrence_window_start,
      path.recurrence_window_end
    );
    if (fromApi) {
      return fromApi;
    }
  }
  return computeMaintenanceRollBounds(path, profileYearToReferenceDate(year));
}

export function listCalendarYearsInRoll(bounds: MaintenanceRollBounds): number[] {
  const years: number[] = [];
  let y = bounds.start.getFullYear();
  const endY = bounds.end.getFullYear();
  while (y <= endY) {
    years.push(y);
    y++;
  }
  return years;
}

export function formatRollYearsLabel(bounds: MaintenanceRollBounds): string {
  const years = listCalendarYearsInRoll(bounds);
  if (years.length === 0) return '';
  if (years.length === 1) return String(years[0]);
  if (years.length === 2) return `${years[0]}, ${years[1]}`;
  return `${years[0]}–${years[years.length - 1]}`;
}

export function dateInRoll(
  dateStr: string | null | undefined,
  bounds: MaintenanceRollBounds | null
): boolean {
  if (!bounds || !dateStr) return false;
  const t = Date.parse(dateStr);
  if (!Number.isFinite(t)) return false;
  const d = parseDateOnly(new Date(t));
  return (
    d.getTime() >= bounds.start.getTime() &&
    d.getTime() <= bounds.end.getTime()
  );
}

export function itemOverlapsRoll(
  itemStart: string | null | undefined,
  itemEnd: string | null | undefined,
  bounds: MaintenanceRollBounds
): boolean {
  const s = itemStart ? parseDateOnly(itemStart).getTime() : 0;
  const e = itemEnd
    ? endOfDay(parseDateOnly(itemEnd)).getTime()
    : Number.MAX_SAFE_INTEGER;
  if (isNaN(s)) return false;
  return s <= bounds.end.getTime() && e >= bounds.start.getTime();
}

/** Map version DTO fields to recurrence input for admin UI. */
export function versionToRollInput(version: {
  recurrence_type?: string | null;
  recurrence_value?: number | null;
  recurrence_unit?: string | null;
  recurrence_calendar_anchor_date?: string | null;
  created_at?: string | null;
  reset_on_fulfillment?: boolean;
  recurrence_window_start?: string | null;
  recurrence_window_end?: string | null;
}): MaintenanceRollRecurrenceInput {
  return {
    recurrence_type: version.recurrence_type,
    recurrence_value: version.recurrence_value,
    recurrence_unit: version.recurrence_unit,
    recurrence_calendar_anchor_date: version.recurrence_calendar_anchor_date,
    version_created_at: version.created_at,
    reset_on_fulfillment: version.reset_on_fulfillment,
    recurrence_window_start: version.recurrence_window_start,
    recurrence_window_end: version.recurrence_window_end,
  };
}

export function pathUserToRollInput(
  path: EducationalPathUserDTO
): MaintenanceRollRecurrenceInput {
  return {
    recurrence_type: path.recurrence_type,
    recurrence_value: path.recurrence_value,
    recurrence_unit: path.recurrence_unit,
    maintenance_started_at: path.maintenance_started_at,
    reset_on_fulfillment: path.reset_on_fulfillment,
    last_maintenance_fulfilled_at: path.last_maintenance_fulfilled_at,
    recurrence_window_start: path.recurrence_window_start,
    recurrence_window_end: path.recurrence_window_end,
  };
}
